export const POS_DEMO_MODE_KEY = 'fiscalpos_pos_demo_mode';
export const POS_UI_PREFERENCES_KEY = 'fiscalpos_pos_ui_preferences';
export const POS_SUPPORT_LOG_KEY = 'fiscalpos_pos_support_log';

const MAX_SUPPORT_LOG_ENTRIES = 150;

const DEFAULT_UI_PREFERENCES = {
  density: 'compact',
  contrast: 'normal',
  motion: 'reduced',
  touchMode: 'comfortable',
  theme: 'light',
};

function canUseWindow() {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function readJsonValue(key, fallbackValue) {
  if (!canUseWindow()) return fallbackValue;

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallbackValue;
    return JSON.parse(raw);
  } catch {
    return fallbackValue;
  }
}

function writeJsonValue(key, value) {
  if (!canUseWindow()) return;

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore local storage write failures on locked-down browsers.
  }
}

export function readPosDemoMode() {
  if (!canUseWindow()) return false;
  return localStorage.getItem(POS_DEMO_MODE_KEY) === '1';
}

export function writePosDemoMode(enabled) {
  if (!canUseWindow()) return;
  localStorage.setItem(POS_DEMO_MODE_KEY, enabled ? '1' : '0');
}

export function readPosUiPreferences() {
  const parsed = readJsonValue(POS_UI_PREFERENCES_KEY, {});
  return {
    ...DEFAULT_UI_PREFERENCES,
    ...(parsed && typeof parsed === 'object' ? parsed : {}),
  };
}

export function writePosUiPreferences(preferences) {
  const nextPreferences = {
    ...DEFAULT_UI_PREFERENCES,
    ...(preferences && typeof preferences === 'object' ? preferences : {}),
  };
  writeJsonValue(POS_UI_PREFERENCES_KEY, nextPreferences);
  return nextPreferences;
}

export function applyPosUiPreferences(preferences) {
  if (typeof document === 'undefined') return;

  const nextPreferences = {
    ...DEFAULT_UI_PREFERENCES,
    ...(preferences && typeof preferences === 'object' ? preferences : {}),
  };
  const root = document.documentElement;
  root.dataset.posDensity = nextPreferences.density;
  root.dataset.posContrast = nextPreferences.contrast;
  root.dataset.posMotion = nextPreferences.motion;
  root.dataset.posTouch = nextPreferences.touchMode;
  root.dataset.posTheme = nextPreferences.theme;
}

export function readSupportLogEntries() {
  const entries = readJsonValue(POS_SUPPORT_LOG_KEY, []);
  return Array.isArray(entries) ? entries : [];
}

export function appendSupportLogEntry(entry) {
  const nextEntry = {
    id: `log_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    level: 'info',
    created_at: new Date().toISOString(),
    ...entry,
  };
  const nextEntries = [nextEntry, ...readSupportLogEntries()].slice(0, MAX_SUPPORT_LOG_ENTRIES);
  writeJsonValue(POS_SUPPORT_LOG_KEY, nextEntries);
  return nextEntries;
}

export function clearSupportLogEntries() {
  writeJsonValue(POS_SUPPORT_LOG_KEY, []);
  return [];
}

export function downloadJsonFile(filename, payload) {
  if (typeof document === 'undefined') return;

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(objectUrl);
}

export function getDefaultUiPreferences() {
  return { ...DEFAULT_UI_PREFERENCES };
}
